<template>
  <div
    class="ce-productSheets"
    :key="`pigment-${pigment.id}-assets-${nonImageAssets.length}`">
    <h2 class="ce-productDetailsHeadline">
      {{ $t('productDetail.assetsHeadline') }}
    </h2>

    <div class="ce-productAssets">
      <div class="ce-productAssets-tabBar">
        <div
          v-for="(assets, category) in assetsByCategory"
          :key="category"
          :class="{active: activeTab === category}">
          <a
            href=""
            @click.prevent="activeTab = category">
            {{ $t(`productDetail.assets.category.${category}`) }}
          </a>
        </div>
      </div>

      <div class="ce-productAssets-tabs">
        <transition-group
          :css="false"
          @enter="enterAnimation"
          @leave="leaveAnimation"
          tag="span">
          <div
            class="ce-productAssets-tab"
            v-for="(assets, category) in assetsByCategory"
            :key="`category-${category}`"
            v-show="activeTab === category">
            <table class="ce-productAssets-table">
              <tbody>
                <template
                  v-for="asset in assets">
                  <MsdsDocument
                    :key="`virtual_${asset.id}`"
                    v-if="asset.type === 'virtual_msds'"
                    :asset="asset"
                    :product="pigment" />

                  <ProductAsset
                    v-else
                    :key="asset.id"
                    :asset="asset"
                    :product="pigment" />
                </template>

                <tr
                  v-if="category === 'general' && pigment.ul_prospector_link"
                  class="ce-productSheet">
                  <td class="ce-productSheet-actions">
                    <div>
                      <a
                        :href="pigment.ul_prospector_link"
                        target="_blank"
                        rel="nofollow noopener">
                        <fa-icon
                          icon="external-link-alt" />
                      </a>
                    </div>
                  </td>
                  <td class="ce-productSheet-label">
                    <a
                      :href="pigment.ul_prospector_link"
                      target="_blank"
                      rel="nofollow noopener">
                      {{ $t('productDetail.ulProspectorTitle') }}
                    </a>
                  </td>
                </tr>

                <tr
                  v-if="category === 'regulatory' && canRequestRegulatoryDocument"
                  class="ce-productSheet">
                  <td class="ce-productSheet-actions">
                    <div />
                  </td>
                  <td class="ce-productSheet-label pt2">
                    <RequestRegulatoryDocumentAction
                      :pigment="pigment"
                      :selected-industry="activeIndustry" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </transition-group>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import slideAnimation from 'mixins/slideAnimation';
import MsdsDocument from 'components/product-detail/MsdsDocument.vue';
import ProductAsset from './ProductAsset.vue';
import RequestRegulatoryDocumentAction from './RequestRegulatoryDocumentAction.vue';

export default {
  data() {
    return {
      activeTab: null
    };
  },
  components: { MsdsDocument, ProductAsset, RequestRegulatoryDocumentAction },
  mixins: [slideAnimation],
  computed: {
    ...mapState(['pigment', 'fieldManifest', 'activeIndustry']),
    ...mapGetters(['nonImageAssets']),
    ...mapGetters('user', ['canRequestRegulatoryDocument']),
    assetsByCategory() {
      const assetsByCategory = {
        regulatory: []
      };

      this.nonImageAssets.forEach((asset) => {
        const manifest = this.fieldManifest[asset.type];

        if (!manifest) {
          return;
        }

        const { documentCategory } = manifest;

        if (!assetsByCategory.hasOwnProperty(documentCategory)) {
          assetsByCategory[documentCategory] = [];
        }

        assetsByCategory[documentCategory].push(asset);
      });

      if (assetsByCategory.regulatory.length === 0) {
        delete assetsByCategory.regulatory;
      }

      // Ensure that the "general" tab is there if the pigment has an ul_prospector_link
      if (this.pigment.ul_prospector_link && !assetsByCategory.general) {
        assetsByCategory.general = [];
      }

      return assetsByCategory;
    }
  },
  mounted() {
    [this.activeTab] = Object.keys(this.assetsByCategory);
  }
};
</script>

<style lang="less">
@import (reference) '~styles/includes/base';

.ce-productAssets {
  margin-top: @space-3;

  .ce-productAssets-tabBar {
    display: flex;
    margin: 0 -@space-2 @space-4;

    > div {
      margin: 0 @space-2;

      a {
        color: @color-mono-70;
        font-weight: bold;
      }

      &.active, &:hover {
        a {
          text-decoration: underline;
          color: @color-text-base;
        }
      }
    }
  }

  .ce-productAssets-table {
    border-spacing: 0;
  }
}
</style>
